/**
 * Mobile Detection & Performance Utilities
 * Detects device capabilities and returns optimized settings
 */

export interface PerformanceSettings {
  isMobile: boolean;
  isLowEnd: boolean;
  dpr: [number, number];
  particleMultiplier: number;
  swarmCount: number;
  starfieldCount: number;
  enablePostProcessing: boolean;
  enableBloom: boolean;
  shadowMapSize: number;
}

// Detect mobile device
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Also check screen width
  const isSmallScreen = window.innerWidth < 768;

  return mobileRegex.test(userAgent.toLowerCase()) || isSmallScreen;
}

// Detect low-end device (rough heuristic)
export function isLowEndDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Check hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 2;

  // Check device memory (if available)
  const memory = (navigator as any).deviceMemory || 4;

  // Low-end: less than 4 cores or less than 4GB RAM
  return cores < 4 || memory < 4;
}

// Get optimized performance settings
export function getPerformanceSettings(): PerformanceSettings {
  const mobile = isMobileDevice();
  const lowEnd = isLowEndDevice();

  if (mobile && lowEnd) {
    // Ultra-low settings for old phones
    return {
      isMobile: true,
      isLowEnd: true,
      dpr: [0.5, 1],
      particleMultiplier: 0.15,
      swarmCount: 500,
      starfieldCount: 3000,
      enablePostProcessing: false,
      enableBloom: false,
      shadowMapSize: 256,
    };
  }

  if (mobile) {
    // Medium settings for modern phones
    return {
      isMobile: true,
      isLowEnd: false,
      dpr: [0.75, 1.5],
      particleMultiplier: 0.3,
      swarmCount: 1500,
      starfieldCount: 8000,
      enablePostProcessing: true,
      enableBloom: true,
      shadowMapSize: 512,
    };
  }

  if (lowEnd) {
    // Medium settings for low-end desktops
    return {
      isMobile: false,
      isLowEnd: true,
      dpr: [1, 1.5],
      particleMultiplier: 0.5,
      swarmCount: 2500,
      starfieldCount: 15000,
      enablePostProcessing: true,
      enableBloom: true,
      shadowMapSize: 1024,
    };
  }

  // Full settings for high-end devices
  return {
    isMobile: false,
    isLowEnd: false,
    dpr: [1, 2],
    particleMultiplier: 1.0,
    swarmCount: 5000,
    starfieldCount: 30000,
    enablePostProcessing: true,
    enableBloom: true,
    shadowMapSize: 2048,
  };
}

// React hook for performance settings
import { useState, useEffect } from 'react';

export function usePerformanceSettings(): PerformanceSettings {
  const [settings, setSettings] = useState<PerformanceSettings>(getPerformanceSettings);

  useEffect(() => {
    // Re-check on resize (tablet rotation, etc.)
    const handleResize = () => {
      setSettings(getPerformanceSettings());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return settings;
}
